/**
 * Client-side error reporter — feeds the admin "User bugs" report.
 *
 * Captures the failures users actually hit (React render crashes, uncaught JS
 * errors, unhandled promise rejections, failed API calls) and POSTs them to
 * /api/client-errors, fire-and-forget. Deduped per signature per session and
 * rate-capped so a loop can't spam. Uses raw fetch (not the api() helper) to
 * avoid an import cycle with http.ts.
 */
import { getIdToken } from "./firebase.ts";

const BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : (import.meta.env.VITE_API_BASE_URL as string);

type Kind = "react" | "window" | "unhandledrejection" | "api";

const seen = new Set<string>();
let windowStart = 0;
let sentThisMinute = 0;
const MAX_PER_MIN = 20;

export async function reportClientError(r: {
  message: string;
  kind: Kind;
  stack?: string;
  extra?: Record<string, string>;
}): Promise<void> {
  try {
    const message = (r.message ?? "").toString().slice(0, 1000);
    if (!message) return;
    // Never report failures of the reporter itself.
    if (message.includes("/api/client-errors")) return;

    const sig = `${r.kind}|${message.slice(0, 120)}|${(r.stack ?? "").split("\n")[1]?.trim() ?? ""}`;
    if (seen.has(sig)) return;

    const now = Date.now();
    if (now - windowStart > 60_000) {
      windowStart = now;
      sentThisMinute = 0;
    }
    if (sentThisMinute >= MAX_PER_MIN) return;
    sentThisMinute += 1;
    seen.add(sig);

    const body = {
      message,
      kind: r.kind,
      stack: r.stack ? String(r.stack).slice(0, 6000) : undefined,
      route: location.pathname,
      url: location.href.slice(0, 500),
      appVersion: (window as unknown as { __SALLY_VERSION?: string }).__SALLY_VERSION,
      extra: r.extra,
    };

    let token: string | null = null;
    try {
      token = await getIdToken();
    } catch {
      /* not signed in / firebase not ready — send anyway (dev) or skip */
    }
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["X-Authorization"] = `Bearer ${token}`;
    else if (import.meta.env.DEV) headers["X-Authorization"] = "Bearer dev-token";
    else return; // no identity in prod → the endpoint would 401; drop quietly

    void fetch(`${BASE}/api/client-errors`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* telemetry must never throw */
  }
}

let initialized = false;
export function initErrorReporter(): void {
  if (initialized) return;
  initialized = true;
  window.addEventListener("error", (e) => {
    const ev = e as ErrorEvent;
    void reportClientError({
      kind: "window",
      message: ev.message || String(ev.error?.message ?? "Script error"),
      stack: ev.error?.stack,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    const message =
      (reason && (reason.message ?? (typeof reason === "string" ? reason : undefined))) ??
      "Unhandled promise rejection";
    void reportClientError({ kind: "unhandledrejection", message, stack: reason?.stack });
  });
}
